// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  CompareProducts,
  MAX_COMPARE,
  getApplicableGoldTagLabel,
  productPhotoMetadata,
  useCompare,
} from '@iheartjane/menu';
import type { CompareProduct } from '@iheartjane/menu';
import { useToast } from '@iheartjane/reefer';
import { EventNames } from '@iheartjane/tracking';
import type { PriceId } from '@iheartjane/types';
import omit from 'lodash-es/omit';
import { useCallback, useMemo } from 'react';
import { InView } from 'react-intersection-observer';
import { useLocation, useParams } from 'react-router';

import { useDeepCompareMemo } from '@jane/shared-ecomm/hooks';
import {
  useBrandSpecialsContext,
  useUserPreferences,
} from '@jane/shared-ecomm/providers';
import { track, trackProductListingTap } from '@jane/shared-ecomm/tracking';
import { LegacyMenuProductCard } from '@jane/shared/components';
import { useJaneUser, useUserSegmentIds } from '@jane/shared/data-access';
import { useShouldShowGold } from '@jane/shared/hooks';
// eslint-disable-next-line no-restricted-imports
import type {
  MenuProduct,
  StoreSpecial as ReduxStoreSpecial,
  StoreSpecial,
} from '@jane/shared/models';
import type { MenuProduct as ZMenuProduct } from '@jane/shared/types';
import {
  defaultWeightIdForMenuProduct,
  deserializeCannabinoids,
  getMaxCartQuantity,
} from '@jane/shared/util';

import { buildClickTrackingEvent } from './buildTrackingEvent';
import { findSpecialForProduct } from './findSpecialForProduct';
import type { ProductCardWithTrackingProps } from './productCardWithTracking.types';

const InViewFlex = styled(InView)({
  display: 'flex',
  flexDirection: 'column',
});

export const ProductCardWithTracking = ({
  algoliaIndexName,
  attemptToAddToCart,
  cardIndex,
  closeCart,
  columnPosition,
  deleteCartItem,
  janeDeviceId,
  menuRowName,
  pdpPath,
  product,
  productLocation,
  rowPosition,
  specials,
  withoutDrawerOnAddToCart,
  ...props
}: ProductCardWithTrackingProps) => {
  const { store, carouselView, listView } = props;
  const c1BundleTag = useFlag(FLAGS.c1BundleTag);
  const compareEnabled = useFlag(FLAGS.c1CompareProducts);
  const {
    isSelected,
    toggleProduct,
    isActive: isCompareActive,
    productCount: compareCount,
  } = useCompare();
  const { data: janeUser } = useJaneUser();
  const toast = useToast();

  const { special_id, brand_special_id } = useParams();
  const { pathname } = useLocation();

  const { brandSpecials } = useBrandSpecialsContext();
  const onBrandSpecialPage = Boolean(brand_special_id);
  const { userLocation, userPreferences = {} } = useUserPreferences();
  const { storeFulfillmentType, storeSearchRadius, storeType } =
    userPreferences;
  const shouldShowGold = useShouldShowGold({
    store,
  });
  const userSegments = useUserSegmentIds();

  const {
    city: storeCity,
    state: storeState,
    name: storeName,
    id: storeId,
  } = store;

  const menuProduct = useDeepCompareMemo<any>(() => {
    const mp = {
      ...deserializeCannabinoids(product.attributes, 'menuProduct'),
      id: product.attributes.product_id,
    };
    return omit(mp, 'product_id') as MenuProduct;
  }, [product]);

  // if we're on a brand special detail page and should hide the tags
  const janeGoldLabel = useMemo(
    () =>
      shouldShowGold && !onBrandSpecialPage
        ? getApplicableGoldTagLabel({
            brandSpecials,
            applicableBrandSpecialIds: menuProduct.applicable_brand_special_ids,
          })
        : undefined,
    [brandSpecials, menuProduct, onBrandSpecialPage, shouldShowGold]
  );

  const pageSpecial = specials?.find(
    (special) => special.id === Number(special_id)
  );

  const currentSpecial =
    pageSpecial?.special_type === 'product'
      ? pageSpecial
      : findSpecialForProduct(
          menuProduct,
          specials as StoreSpecial[],
          c1BundleTag
        );

  const clickTrackingEvent = buildClickTrackingEvent({
    indexName: algoliaIndexName,
    store,
    menuProduct,
    carouselView,
    rowPosition,
    cardIndex,
    columnPosition,
    listView,
    menuRowName,
    userSegments,
  });

  const handleTrackImpression = useCallback(
    async (isInView: boolean) => {
      if (isInView && !props.isMenuTopRow) {
        product.impress();
      }
    },
    [product]
  );

  const handleMenuProductCardClick = useMemo(() => {
    return (selectedWeight: PriceId | null) => {
      product.click();

      clickTrackingEvent && track(clickTrackingEvent);

      const inventoryCount =
        !!menuProduct && !!selectedWeight
          ? getMaxCartQuantity(menuProduct, selectedWeight)
          : null;

      trackProductListingTap({
        almostGone: inventoryCount < 4 ? inventoryCount : null,
        cityState: userLocation?.cityState,
        columnPosition,
        product: menuProduct,
        productLocation,
        rowPosition,
        source: pathname,
        storeId: String(storeId),
        storeName,
        storeCity: storeCity || undefined,
        storeState: storeState || undefined,
        storeFulfillmentType,
        storeSearchRadius,
        storeType,
        userSegments,
        zipcode: userLocation?.zipcode,
      });
    };
  }, [
    clickTrackingEvent,
    columnPosition,
    janeDeviceId,
    menuProduct,
    pathname,
    product,
    productLocation,
    rowPosition,
    storeCity,
    storeFulfillmentType,
    storeId,
    storeName,
    storeSearchRadius,
    storeState,
    storeType,
    userLocation?.cityState,
    userLocation?.zipcode,
    userSegments,
  ]);

  const productId = menuProduct.id;
  const compareProduct = useMemo<CompareProduct | null>(() => {
    if (!compareEnabled) return null;
    const attrs = product.attributes;
    const { url: imageUrl } = productPhotoMetadata(attrs);
    const price = attrs.price_each != null ? Number(attrs.price_each) : null;
    const discounted =
      attrs.discounted_price_each != null
        ? Number(attrs.discounted_price_each)
        : null;
    const hasDiscount =
      discounted != null && price != null && discounted < price;

    return {
      id: productId,
      name: attrs.name,
      brand: attrs.brand,
      kind: attrs.kind,
      category: attrs.category,
      imageUrl,
      price: hasDiscount ? discounted : price,
      originalPrice: hasDiscount ? price : null,
      percentThc: attrs.percent_thc != null ? Number(attrs.percent_thc) : null,
      percentCbd: attrs.percent_cbd != null ? Number(attrs.percent_cbd) : null,
      thcDosageMg: menuProduct.thc_dosage_milligrams ?? null,
      cbdDosageMg: menuProduct.cbd_dosage_milligrams ?? null,
      feelings: attrs.feelings ?? [],
      activities: attrs.activities ?? [],
      flavors: attrs.flavors?.map((f) => f.title) ?? [],
      aggregateRating: attrs.aggregate_rating,
      reviewCount:
        attrs.review_count != null ? Number(attrs.review_count) : null,
      weight: attrs.dosage ?? null,
      availableWeights: (attrs.available_weights as string[]) ?? [],
      url: pdpPath?.({ id: productId, brand: attrs.brand }, undefined) ?? null,
    };
  }, [compareEnabled, pdpPath, productId, product.attributes]);

  const handleCompareToggle = useCallback(() => {
    if (!compareProduct) return;

    const userType = janeUser?.user?.authenticated ? 'registered' : 'guest';
    const compareProductProperties = {
      productId: compareProduct.id,
      productName: compareProduct.name,
      productBrand: compareProduct.brand,
      productCategory: compareProduct.category,
      productKind: compareProduct.kind,
      storeId: String(store.id),
      storeName: store.name,
      url: window.location.href,
      userType,
    } as const;

    const addToCart = () => {
      const priceId = defaultWeightIdForMenuProduct({ menuProduct }) as PriceId;
      attemptToAddToCart({
        count: 1,
        location: 'menu',
        menuProduct: menuProduct as ZMenuProduct,
        price_id: priceId,
        store,
      });
      product.click({ isAddToCart: true });
      track({
        event: EventNames.AddedCompareModalProductToCart,
        priceUnit: priceId,
        ...compareProductProperties,
        url: window.location.href,
      });
    };

    // navigation is handled by the modal's links — this only tracks the click
    const view = () => {
      product.click();
      track({
        event: EventNames.ClickedCompareModalProduct,
        ...compareProductProperties,
        url: window.location.href,
      });
    };

    const isAdding = !isSelected(compareProduct.id);
    const success = toggleProduct(compareProduct, { addToCart, view });
    if (!success) {
      toast.add({
        label: `Maximum ${MAX_COMPARE} products for comparison`,
        time: 2500,
      });
    } else if (isAdding) {
      track({
        event: EventNames.AddedProductToCompare,
        compareCount: compareCount + 1,
        ...compareProductProperties,
      });
    }
  }, [
    attemptToAddToCart,
    compareCount,
    compareProduct,
    isSelected,
    janeUser?.user?.authenticated,
    menuProduct,
    product,
    store,
    toggleProduct,
    toast,
  ]);

  const compareOverlay = useMemo(() => {
    if (!isCompareActive || !compareProduct) return undefined;
    return (
      <CompareProducts.Toggle
        isSelected={isSelected(productId)}
        onToggle={handleCompareToggle}
        productName={compareProduct.name}
      />
    );
  }, [
    isCompareActive,
    compareProduct,
    handleCompareToggle,
    isSelected,
    productId,
  ]);

  return (
    <InViewFlex
      data-testid="menu-product-card-with-tracking"
      delay={1000}
      onChange={(isInView) => handleTrackImpression(isInView)}
      threshold={0.5}
      triggerOnce
    >
      <LegacyMenuProductCard
        compareOverlay={compareOverlay}
        currentSpecial={currentSpecial as ReduxStoreSpecial}
        pdpPath={pdpPath}
        product={menuProduct}
        productInstance={product}
        onAddToCart={(addData) => {
          attemptToAddToCart({
            ...addData,
            columnPosition,
            menuProduct: addData.menuProduct as ZMenuProduct,
            productLocation,
            rowPosition,
            withoutDrawer: withoutDrawerOnAddToCart,
          });
          product.click({ isAddToCart: true });
        }}
        onDeleteFromCart={(deleteData) => {
          deleteCartItem(deleteData.itemId, deleteData.selectedWeight);
        }}
        onCloseCart={() => closeCart()}
        onClick={handleMenuProductCardClick}
        userLocation={userLocation}
        janeGoldLabel={janeGoldLabel}
        brandSpecials={brandSpecials}
        {...props}
      />
    </InViewFlex>
  );
};
